import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config';

interface AgentStatusWidgetProps {
  agentId: string;
  onStatusChange?: (status: string) => void;
  active?: boolean;
}

type AgentStatus = 'available' | 'busy' | 'offline';

export const AgentStatusWidget: React.FC<AgentStatusWidgetProps> = ({
  agentId,
  onStatusChange,
  active = true,
}) => {
  const [status, setStatus] = useState<AgentStatus>('offline');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastManualUpdate, setLastManualUpdate] = useState<number>(0);

  useEffect(() => {
    if (!active) return;

    // Fetch initial status
    fetchAgentStatus();

    // Poll for status updates every 60 seconds to reduce API traffic.
    const interval = setInterval(() => {
      // Don't poll if a manual update happened in the last 3 seconds
      if (Date.now() - lastManualUpdate > 3000) {
        fetchAgentStatus();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [active, agentId, lastManualUpdate]);

  const fetchAgentStatus = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.AGENT.STATUS}?user_id=${agentId}`);
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      // Backend returns the entire row object, extract the status field
      const statusValue = typeof data.status === 'string' ? data.status : data.status?.status;
      setStatus(statusValue === 'available' || statusValue === 'busy' || statusValue === 'offline' ? statusValue : 'offline');
      setError(null);
    } catch (error) {
      console.error('Failed to fetch agent status:', error);
      setError('Could not load agent status');
    }
  };

  const updateStatus = async (newStatus: AgentStatus) => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.AGENT.STATUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: agentId,
          status: newStatus,
        }),
      });

      if (response.ok) {
        setStatus(newStatus);
        setError(null);
        setLastManualUpdate(Date.now());
        if (onStatusChange) {
          onStatusChange(newStatus);
        }
      } else {
        const message = await response.text();
        console.error('Failed to update status:', message);
        setError('Could not update agent status');
      }
    } catch (error) {
      console.error('Failed to update agent status:', error);
      setError('Could not update agent status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (currentStatus: AgentStatus) => {
    switch (currentStatus) {
      case 'available':
        return 'bg-green-500';
      case 'busy':
        return 'bg-yellow-500';
      case 'offline':
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (currentStatus: AgentStatus) => {
    switch (currentStatus) {
      case 'available':
        return 'Available';
      case 'busy':
        return 'Busy';
      case 'offline':
      default:
        return 'Offline';
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">Agent Status</h3>
        <div className="flex items-center gap-1.5">
          <div className={`h-2.5 w-2.5 rounded-full ${getStatusColor(status)}`} />
          <span className="text-xs font-medium text-slate-700">{getStatusText(status)}</span>
        </div>
      </div>

      {error && (
        <div className="mb-2 rounded-md bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5">
        <button
          onClick={() => updateStatus('available')}
          disabled={loading}
          className={`rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
            status === 'available'
              ? 'bg-green-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          Available
        </button>
        <button
          onClick={() => updateStatus('busy')}
          disabled={loading}
          className={`rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
            status === 'busy'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          Busy
        </button>
        <button
          onClick={() => updateStatus('offline')}
          disabled={loading}
          className={`rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
            status === 'offline'
              ? 'bg-gray-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          Offline
        </button>
      </div>
    </div>
  );
};
